import { useContext, useMemo } from 'react';

import {
  Country,
  InputMaybe,
  UpdateCreditAppOnDealMutationVariables,
} from '../../gql/generated/graphql';

import { ModalContext } from '../../libs/contextLib';

export const getPersonCreditAppValues = (person: CreditAppOnDeal['customer']) => ({
  id: person.id,
  auth0_id: person.auth0_id,
  dob: person.dob,
  ssn_id: person.ssn_id,
  has_full_ssn: person.has_full_ssn,
  has_itin: person.has_itin,
  first_name: person.first_name,
  middle_name: person.middle_name,
  last_name: person.last_name,
  name_suffix: person.name_suffix,
  phone_number: person.phone_number,
  home_phone_number: person.home_phone_number,
  email: person.email,
  marital_status: person.marital_status,
  relation_to_buyer: person.relation_to_buyer,
  lives_with_other_buyer: person.lives_with_other_buyer,
  wi_notification_agreement: person.wi_notification_agreement,
  mailingAddressSameHasResidential: !person.mailing_address,
  address: {
    address_type: person.address?.address_type,
    address_line: person.address?.address_line,
    address_line_2: person.address?.address_line_2,
    city: person.address?.city,
    county: person.address?.county,
    state: person.address?.state,
    zip: person.address?.zip,
    residence_type: person.address?.residence_type,
    monthly_payment: person.address?.monthly_payment,
    months_at_home: person.address?.months_at_home,
    years_at_home: person.address?.years_at_home,
  },
  ...(!!person.prev_address && {
    prev_address: {
      address_type: person.prev_address.address_type,
      address_line: person.prev_address.address_line,
      address_line_2: person.prev_address.address_line_2,
      city: person.prev_address.city,
      county: person.prev_address.county,
      state: person.prev_address.state,
      zip: person.prev_address.zip,
      residence_type: person.prev_address.residence_type,
      months_at_home: person.prev_address.months_at_home,
      years_at_home: person.prev_address.years_at_home,
    },
  }),
  mailing_address: person.mailing_address
    ? {
        address_type: person.mailing_address.address_type,
        address_line: person.mailing_address.address_line,
        address_line_2: person.mailing_address.address_line_2,
        city: person.mailing_address.city,
        state: person.mailing_address.state,
        county: person.mailing_address.county,
        zip: person.mailing_address.zip,
      }
    : null,
  employment: {
    status: person.employment?.status,
    name: person.employment?.name,
    job_title: person.employment?.job_title,
    phone_number: person.employment?.phone_number,
    years_at_job: person.employment?.years_at_job,
    months_at_job: person.employment?.months_at_job,
    gross_income: person.employment?.gross_income,
    pay_frequency: person.employment?.pay_frequency,
  },
  ...(!!person.prev_employment && {
    prev_employment: {
      name: person.prev_employment.name,
      job_title: person.prev_employment.job_title,
      years_at_job: person.prev_employment.years_at_job,
      months_at_job: person.prev_employment.months_at_job,
    },
  }),
  proof_of_insurance: {
    company_name: person.proof_of_insurance?.company_name,
    policy_number: person.proof_of_insurance?.policy_number,
    expires: person.proof_of_insurance?.expires,
  },
});

export const getContactCreditAppValues = (contact: NonNullable<CreditAppOnDeal['contact']>) => ({
  first_name: contact.first_name,
  middle_name: contact.middle_name,
  last_name: contact.last_name,
  phone_number: contact.phone_number,
  email: contact.email,
  relation_to_buyer: contact.relation_to_buyer,
});

export const getOriginalLesseeCreditAppValues = (
  original_lessee: NonNullable<CreditAppOnDeal['original_lessee']>,
) => ({
  first_name: original_lessee.first_name,
  last_name: original_lessee.last_name,
  phone_number: original_lessee.phone_number,
  email: original_lessee.email,
  address_line: original_lessee.address_line,
  ssn_id: original_lessee.ssn_id,
  city: original_lessee.city,
  state: original_lessee.state,
  zip: original_lessee.zip,
  county: original_lessee.county,
});

export type CreditAppOnDeal = UpdateCreditAppOnDealMutationVariables['input'] & {
  customer: UpdateCreditAppOnDealMutationVariables['input']['customer'] & {
    wi_notification_agreement?: boolean;
    mailingAddressSameHasResidential?: boolean;
    auth0_id?: string | null;
  };
  cobuyer?: InputMaybe<
    UpdateCreditAppOnDealMutationVariables['input']['cobuyer'] & {
      wi_notification_agreement?: boolean;
      mailingAddressSameHasResidential?: boolean;
      auth0_id?: string | null;
    }
  >;
  original_lessee?: InputMaybe<
    UpdateCreditAppOnDealMutationVariables['input']['original_lessee'] & {
      has_full_ssn?: boolean;
    }
  >;
  addCobuyer: boolean;
  addContact: boolean;
  addSecondContact: boolean;
  addOriginalLessee: boolean;
  // Read-only deal country; drives address and SSN validation and is never sent back.
  country?: Country | null;
};

const useCreditAppInitialValues = <TDeal extends CreditAppOnDeal>(formValues: TDeal) => {
  const { modalCloseCount } = useContext(ModalContext);
  return useMemo<CreditAppOnDeal>(
    () => ({
      id: formValues.id,
      state: formValues.state,
      country: formValues.country,
      odometer: formValues.odometer_status,
      addCobuyer: formValues.addCobuyer,
      addContact: formValues.addContact,
      addSecondContact: formValues.addSecondContact,
      addOriginalLessee: formValues.addOriginalLessee,
      customer: getPersonCreditAppValues(formValues.customer),
      ...(formValues.cobuyer && { cobuyer: getPersonCreditAppValues(formValues.cobuyer) }),
      ...(formValues.contact && { contact: getContactCreditAppValues(formValues.contact) }),
      ...(formValues.second_contact && {
        second_contact: getContactCreditAppValues(formValues.second_contact),
      }),
      ...(formValues.original_lessee && {
        original_lessee: getOriginalLesseeCreditAppValues(formValues.original_lessee),
      }),
      financial_info: {
        term: formValues.financial_info?.term,
        money_down: formValues.financial_info?.money_down,
        buyer_not_lessee: formValues.financial_info?.buyer_not_lessee,
      },
    }),
    // DIEGO - DEBUG
    // TODO: By adding the `state` as dependency, we could probably remove the `Force initial values recalculation` logic that is using the `modalCloseCount`.
    [modalCloseCount, formValues.state],
  );
};

export default useCreditAppInitialValues;
